import React, { useState } from 'react';
import { Row, Col, Card, CardBody, Table, Alert } from 'reactstrap';
import useFreightRequest from '../../hooks/useFreightRequest';
import SubmitConfirmModal from '../SubmitConfirmModal';
import {
  displayValue,
  formatProposalDeadline,
  getContractPeriodProps,
  getContractTypeLabel,
  getProcurementTypeLabel,
} from '../../utils/tenderOpeningHelpers';
import {
  buildParcelFreightExportRows,
  PARCEL_FREIGHT_EXPORT_COLUMNS,
} from '../../utils/parcelFreightHelpers';
import { formatDateToDDMMYYYY } from '../../../../utils/dateformatter';
import ActionButton from '../../../../Components/Common/ActionButton';
import { EmptyState } from '../../../../Components/Common/ListStates';
import {
  SkeletonCard,
  SkeletonTable,
} from '../../../../Components/Common/Skeleton';

const InfoField = ({ label, value, col = 4 }) => (
  <Col xs={6} md={col}>
    <div className='border-start border-2 border-primary ps-3'>
      <div className='text-muted small fw-semibold mb-1'>{label}</div>
      <p className='text-dark fs-13 mb-0 lh-base'>{displayValue(value)}</p>
    </div>
  </Col>
);

const ParcelFreightTable = ({ parcels }) => {
  if (!parcels?.length) {
    return (
      <EmptyState
        compact
        icon='ri-ship-line'
        title='No parcels to show'
        emptyHint='This procurement has no parcel delivery terms yet, so there is nothing to request freight for.'
      />
    );
  }

  const rows = buildParcelFreightExportRows(parcels);

  if (!rows.length) {
    return (
      <EmptyState
        compact
        icon='ri-ship-line'
        title='No cargo needs freight'
        emptyHint='No cargo combines FOB with CFR, DAP or CIF, so no freight availability rows apply.'
      />
    );
  }

  return (
    <div className='table-responsive'>
      <Table
        striped
        bordered
        hover
        className='align-middle text-nowrap mb-0'
        aria-label='Freight availability'
      >
        <thead className='table-light'>
          <tr>
            {PARCEL_FREIGHT_EXPORT_COLUMNS.map(column => (
              <th key={column.key} className='fw-semibold small'>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {PARCEL_FREIGHT_EXPORT_COLUMNS.map(column => {
                const cellValue = row[column.key];
                return (
                  <td
                    key={column.key}
                    className={`small${
                      ['freightLumpsum', 'laytime', 'demurrage', 'r2'].includes(
                        column.key
                      )
                        ? ' text-muted fst-italic'
                        : ''
                    }`}
                  >
                    {column.key === 'aldAdd' &&
                    typeof cellValue === 'string' &&
                    cellValue.includes(' - ') ? (
                      <>
                        {cellValue.split(' - ')[0]} -<br />
                        {cellValue.split(' - ')[1]}
                      </>
                    ) : (
                      cellValue
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const RequestFreightAvailability = ({
  procId,
  procurement,
  isLoadingProcurement,
  onRefreshProcurement,
  parcels = [],
  isLoadingParcels = false,
}) => {
  const {
    isSubmitting,
    sendFreightRequest,
    exportParcelFreightToExcel,
    isExportingExcel,
  } = useFreightRequest({
    procurementId: procId,
    procurement,
    onRefreshProcurement,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isSubmitted = procurement?.isReqFreightAvailabilityCompleted === true;
  const periodProps = getContractPeriodProps(procurement);
  const handleSendRequest = async () => {
    await sendFreightRequest();
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <>
        <SkeletonCard lines={2} className='mb-4' />
        <SkeletonCard lines={5} className='mb-4' />
        <SkeletonTable
          rows={3}
          columns={PARCEL_FREIGHT_EXPORT_COLUMNS.length}
        />
      </>
    );
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          <Card className='border shadow-sm mb-4'>
            <CardBody>
              <div className='mb-3'>
                <h5 className='fw-semibold mb-1'>
                  Request Freight Availability
                </h5>
                <p className='text-muted small mb-0'>
                  Freight cost and availability request details for this
                  procurement.
                </p>
              </div>

              <hr className='my-3 border-secondary opacity-25' />

              <Row className='g-3'>
                <InfoField
                  label='Freight Request Number'
                  value={procurement?.freightRequestNumber}
                />
                <InfoField
                  label='Freight Request Date'
                  value={
                    procurement?.freightRequestDate
                      ? formatDateToDDMMYYYY(procurement.freightRequestDate)
                      : null
                  }
                />
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm mb-4'>
            <CardBody>
              <div className='mb-3'>
                <h5 className='fw-semibold mb-1'>Procurement Information</h5>
                <p className='text-muted small mb-0'>
                  Procurement details linked to this freight request.
                </p>
              </div>

              <hr className='my-3 border-secondary opacity-25' />

              <Row className='g-3'>
                <InfoField
                  label='Procurement Type'
                  value={procurement?.procurementTypeHeaderLabel}
                />
                <InfoField
                  label='Title Procurement'
                  value={procurement?.titleProcurement}
                />
                <InfoField
                  label='Proc Type'
                  value={getProcurementTypeLabel(procurement?.procurementType)}
                />
                <InfoField
                  label='Contract Type'
                  value={getContractTypeLabel(procurement?.contractType)}
                />
                <InfoField
                  label={periodProps.startLabel}
                  value={periodProps.startValue}
                />
                {periodProps.endLabel && (
                  <InfoField
                    label={periodProps.endLabel}
                    value={periodProps.endValue}
                  />
                )}
                <InfoField
                  label='Submission Deadline (Jakarta Local Time)'
                  value={formatProposalDeadline(
                    procurement?.proposalSubmissionDeadline
                  )}
                  col={6}
                />
                <InfoField
                  label='Description'
                  value={procurement?.description}
                  col={12}
                />
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm mb-4'>
            <CardBody>
              <Alert
                color='info'
                className='d-flex align-items-start gap-2 border-0 bg-light text-primary'
              >
                <i className='ri-information-line flex-shrink-0 mt-1'></i>
                <span className='small'>
                  The detailed procurement plan information below can be
                  downloaded in Excel format to be used as an attachment in the
                  submission of the Freight Cost and Availability Request
                  Memorandum.
                </span>
              </Alert>

              <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3'>
                <div>
                  <h5 className='fw-semibold mb-1'>Parcel</h5>
                  <p className='text-muted small mb-0'>
                    Delivery terms mapped from procurement planning.
                  </p>
                </div>
                <ActionButton
                  variant='secondary'
                  size='sm'
                  icon='ri-file-excel-2-line'
                  disabled={isLoadingParcels}
                  loading={isExportingExcel}
                  onClick={() => exportParcelFreightToExcel(parcels)}
                >
                  Export to Excel
                </ActionButton>
              </div>

              <hr className='my-2 border-secondary opacity-25' />

              {isLoadingParcels ? (
                <SkeletonTable
                  rows={3}
                  columns={PARCEL_FREIGHT_EXPORT_COLUMNS.length}
                />
              ) : (
                <ParcelFreightTable parcels={parcels} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {!isSubmitted && (
        <div className='req-actionbar flex-wrap'>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            loading={isSubmitting}
            onClick={() => setShowConfirmModal(true)}
          >
            Send Request Freight Availability
          </ActionButton>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await handleSendRequest();
        }}
        title='Send freight availability request?'
        message='The request is sent right away and this step is then locked. It cannot be undone.'
        confirmLabel='Send request'
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default RequestFreightAvailability;
